import { Table, TableRow, TableCell } from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import { Fragment } from 'react';
import { Flipper } from 'react-flip-toolkit';

import { tss } from 'tss-react/mui';

import type { FileFieldsFragment } from '@/gql/graphql';

import type { OwnerPermisson } from '@/modules/files/hooks/useFilePermissions';

import { graphql, type DocumentType } from '@/gql';
import { sortFiles } from '@/utils/file/fileUtils';

import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import type { FileManagerUploadingState } from '../FileManagerUploadingBar';

import { FileRow } from './FileRow';
import { FileManagerTableHead } from './FileManagerTableHead';

const FILE_MANAGER_CONTENT_TABLE_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileManagerContentTableFilesForOwnerFields on FilesForOwner {
    ...fileRowFields
    owner {
      ... on Group {
        _id
        type {
          _id
          showFilesForChildrenWithTypesConnection {
            edges {
              node {
                _id
              }
            }
          }
        }
      }
      ... on Service {
        _id
        type {
          _id
        }
      }
    }
    filesConnection {
      edges {
        node {
          ...fileFields
        }
      }
    }
  }
`);

interface Props {
  filesForOwner:
    | DocumentType<typeof FILE_MANAGER_CONTENT_TABLE_FILES_FOR_OWNER_FRAGMENT>
    | null
    | undefined;
  ownerPermissions: OwnerPermisson[];
  rootOwnerId: string;
  sort: string | undefined;
  allSelected: boolean;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
}

const useStyles = tss.create(({ theme }) => ({
  pinned: {
    backgroundColor: theme.secondaryBackground,
    fontStyle: 'italic',
    borderLeftColor: theme.secondaryBackground,
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
  },
  unpinnedSpacer: {
    '&:first-child': {
      display: 'none',
    },
    backgroundColor: theme.secondaryBackground,
    borderLeftColor: theme.secondaryBackground,
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
  },
}));

export const FileManagerContentTable = ({
  filesForOwner,
  ownerPermissions,
  rootOwnerId,
  sort,
  allSelected,
  setUploadingState,
}: Props) => {
  const { filesConnection, owner } = filesForOwner ?? {};

  const { classes } = useStyles();

  const files = extractConnectionProps(filesConnection);

  const sortedFiles = sortFiles(files, sort, true);

  const ownerType = owner?.__typename === 'Group' ? owner?.type : null;

  // We can restrict visibility if the group type has any
  // showFilesForChildrenWithTypes on it
  const canRestrictVisibility = Boolean(
    ownerType?.showFilesForChildrenWithTypesConnection.edges.length,
  );

  const isFirstPinnedFile = (file: FileFieldsFragment) => {
    if (!file.pinned) {
      return false;
    }

    const idx = sortedFiles.indexOf(file);
    return idx >= 0 && !sortedFiles[idx - 1]?.pinned;
  };

  const isFirstUnpinnedFile = (file: FileFieldsFragment) => {
    if (file.pinned) {
      return false;
    }

    const idx = sortedFiles.indexOf(file);
    if (idx === 0) {
      return true;
    }

    return idx > 0 && Boolean(sortedFiles[idx - 1].pinned);
  };

  return (
    <Table size="small" sx={{ minWidth: 768, overflowX: 'auto' }}>
      <FileManagerTableHead
        sort={sort}
        canRestrictVisibility={canRestrictVisibility}
        selected={allSelected}
      />
      <Flipper
        element="tbody"
        flipKey={sortedFiles.map((file, index) => file._id ?? index).join(':')}
      >
        {sortedFiles.map((file, index) => {
          return (
            <Fragment key={file._id ?? index}>
              {isFirstPinnedFile(file) && (
                <TableRow selected={allSelected}>
                  <TableCell colSpan={5} className={classes.pinned}>
                    Pinned items
                  </TableCell>
                </TableRow>
              )}
              {isFirstUnpinnedFile(file) && (
                <TableRow className={classes.unpinnedSpacer}>
                  <TableCell colSpan={5}>&nbsp;</TableCell>
                </TableRow>
              )}
              {filesForOwner ? (
                <FileRow
                  file={file}
                  rootOwnerId={rootOwnerId}
                  filesForOwner={filesForOwner}
                  ownerPermissions={ownerPermissions}
                  canRestrictVisibility={canRestrictVisibility}
                  selected={allSelected}
                  setUploadingState={setUploadingState}
                />
              ) : null}
            </Fragment>
          );
        })}
      </Flipper>
    </Table>
  );
};
